import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService, IApiOption } from '@red/api';
import { PaginationAdapter } from '@red/data-access';
import { map } from 'rxjs/operators';
import { PaymentReminderModel } from '@shared/data-access/models/payment-reminder.model';
import { DebtRecoveryOptionDto, PaymentReminderBatchDownloadLetterDto, PaymentReminderFiltersDto, PaymentReminderIssueDto, PaymentReminderSendPreLodEmailDto, PaymentReminderRevertDto, PaymentReminderSkipDto, WriteOffDto } from '@shared/data-access/dto/payment-reminder.dto';
import { EReminderStage } from '@shared/data-access/enums/payment-reminder.enum';
import { IJobLetterBatchResponse, IJobLetterBatchStatusResponse } from '@shared/data-access/interfaces';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PaymentReminderApiService {
  static ROOT_POINT = 'storage/payment-reminders';
  static CREATE_POINT = 'storage/payment-reminders/create-eligible';
  static UPDATE_POINT = 'storage/payment-reminders/update-eligible';
  static SEND_BACTH_EMAIL_POINT = 'storage/payment-reminders/send-batch-email';
  static DOWNLOAD_BATCH_LETTERS_POINT = 'storage/payment-reminders/letters/batch';

  constructor(private _apiService: ApiService, private _httpClient: HttpClient) { }

  search(query: PaymentReminderFiltersDto, option?: IApiOption) {
    return this._apiService.get(PaymentReminderApiService.ROOT_POINT, query, { ...option, removeEmpty: { enable: true } }).pipe(map(data => new PaginationAdapter(PaymentReminderModel, data)));
  }

  updateRemarks(id: number, remarks: string, option?: IApiOption) {
    return this._apiService.patch(PaymentReminderApiService.ROOT_POINT + `/${id}/remarks`, { remarks }, option);
  }

  viewLetter(data: { id: number; stage: EReminderStage; type: string }) {
    return this._apiService.post(`${PaymentReminderApiService.ROOT_POINT}/:id/view-letter`, data, {
      pretreatmentResponse: false,
      requestOptions: { responseType: 'arraybuffer' },
    });
  }
  issueReminder(id: number, data?: PaymentReminderIssueDto) {
    return this._apiService.patch(PaymentReminderApiService.ROOT_POINT + `/${id}/issue`, data);
  }

  sendEmail(id: number, stage: string): Observable<unknown> {
    return this._apiService.post(PaymentReminderApiService.ROOT_POINT + `/${id}/send-email`, { stage });
  }

  sendPreLodEmail(id: number, data: PaymentReminderSendPreLodEmailDto): Observable<unknown> {
    return this._apiService.post(PaymentReminderApiService.ROOT_POINT + `/${id}/send-pre-lod-email`, data);
  }

  sendBatchEmail(ids: number[], stage: string): Observable<unknown> {
    return this._apiService.post(PaymentReminderApiService.SEND_BACTH_EMAIL_POINT, { ids, stage });
  }

  skipAction(id: number, data: PaymentReminderSkipDto): Observable<unknown> {
    return this._apiService.patch(`${PaymentReminderApiService.ROOT_POINT}/${id}/skip`, data);
  }

  revert(id: number, data: PaymentReminderRevertDto): Observable<unknown> {
    return this._apiService.patch(`${PaymentReminderApiService.ROOT_POINT}/${id}/revert`, data);
  }

  updateDebtRecoveryOption(id: number, data: DebtRecoveryOptionDto): Observable<unknown> {
    return this._apiService.patch(`${PaymentReminderApiService.ROOT_POINT}/${id}/debt-recovery`, data);
  }

  writeOff(id: number, data: WriteOffDto): Observable<unknown> {
    return this._apiService.patch(`${PaymentReminderApiService.ROOT_POINT}/${id}/write-off`, data);
  }

  reactivate(id: number, data: PaymentReminderSkipDto): Observable<unknown> {
    return this._apiService.patch(`${PaymentReminderApiService.ROOT_POINT}/${id}/reactivate`, data);
  }

  create(): Observable<PaymentReminderModel> {
    return this._apiService.post(PaymentReminderApiService.CREATE_POINT).pipe(map((res) => PaymentReminderModel.fromJson(res) as PaymentReminderModel));
  }

  update(): Observable<PaymentReminderModel> {
    return this._apiService.patch(PaymentReminderApiService.UPDATE_POINT).pipe(map((res) => PaymentReminderModel.fromJson(res) as PaymentReminderModel));
  }


  viewDebtRecoveryLetter(data: { id: number; debtRecoveryOption: string; type: string }) {
    return this._apiService.post(`${PaymentReminderApiService.ROOT_POINT}/:id/view-debt-recovery-letter`, data, {
      pretreatmentResponse: false,
      requestOptions: { responseType: 'arraybuffer' },
    });
  }

  getJobLetterBatch(query: PaymentReminderBatchDownloadLetterDto): Observable<IJobLetterBatchResponse> {
    return this._apiService.post(PaymentReminderApiService.DOWNLOAD_BATCH_LETTERS_POINT, query)
  }

  getJobStatusLetterBatch(jobId: string): Observable<IJobLetterBatchStatusResponse> {
    return this._apiService.get(`${PaymentReminderApiService.DOWNLOAD_BATCH_LETTERS_POINT}/${jobId}`, {})
  }

  downloadJobLetterChunk(url: string): Observable<Blob> {
    return this._httpClient.get(url, { responseType: 'blob' });
  }

}
